import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

type CredentialSecret = {
  id: string;
  name: string;
  authProvider: string;
  data: Record<string, unknown>;
  metadata: Record<string, unknown> | null;
  isDefault: boolean;
  role: string | null;
  roleContext: string | null;
  tags: string[];
  notes: string | null;
  /** Resolved login start URL (environment URL + relative login path) */
  loginUrl: string | null;
  loginInstructions: string | null;
  sessionVerificationGuidance: string | null;
  lastUsedAt: string | null;
  updatedAt: string;
};

type CredentialSecretQueryResult = {
  credential: CredentialSecret | null;
  accessDenied: boolean;
  notFound: boolean;
};

type CredentialSecretQueryOptions = Omit<
  UseQueryOptions<CredentialSecretQueryResult>,
  'queryKey' | 'queryFn' | 'enabled'
>;

export function useCredentialSecret(
  propertyId: string | null,
  credentialId: string | null,
  options?: CredentialSecretQueryOptions & { enabled?: boolean }
) {
  const { enabled = true, ...queryOptions } = options ?? {};
  return useQuery<CredentialSecretQueryResult>({
    queryKey: ['credential-secret', propertyId, credentialId],
    enabled: Boolean(propertyId && credentialId && enabled),
    queryFn: async () => {
      if (!propertyId || !credentialId) {
        return { credential: null, accessDenied: false, notFound: false };
      }

      const response = await fetch(
        `${API_URL}/org/properties/${propertyId}/credentials/${credentialId}`,
        { credentials: 'include' }
      );

      if (response.status === 403) {
        return { credential: null, accessDenied: true, notFound: false };
      }

      if (response.status === 404) {
        return { credential: null, accessDenied: false, notFound: true };
      }

      if (!response.ok) {
        throw await toApiError(response, 'Failed to load credential secret');
      }

      const body = await response.json();
      return {
        credential: body.credential as CredentialSecret,
        accessDenied: false,
        notFound: false,
      };
    },
    ...queryOptions,
  });
}
